/**
 * Contract React Query Hooks
 *
 * Provides React Query hooks for contract data fetching and mutations
 */

import type {
  BillingPeriod,
  ContractStatus,
} from '@monorepo/shared/schemas/common'
import type {
  ContractDiscount,
  ContractQuery,
  ContractTax,
  CreateContract,
  UpdateContract,
} from '@monorepo/shared/schemas/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  type AddAddonRequest,
  type ContractAddon,
  contractService,
} from '@/features/customers/services/contractService'
import type { ApiError } from '@/shared/types/api'

// ============================================================================
// FILTER TYPES
// ============================================================================

export interface ContractFilters {
  search?: string
  status?: ContractStatus | 'all'
  customer_id?: string | 'all'
  plan_id?: string | 'all'
  billing_interval?: BillingPeriod | 'all'
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const contractKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractKeys.all, 'list'] as const,
  list: (query?: ContractQuery) => [...contractKeys.lists(), query] as const,
  details: () => [...contractKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractKeys.details(), id] as const,
  lineItems: (id: string) =>
    [...contractKeys.detail(id), 'line-items'] as const,
  addons: (id: string) => [...contractKeys.detail(id), 'addons'] as const,
  discounts: (id: string) => [...contractKeys.detail(id), 'discounts'] as const,
  taxes: (id: string) => [...contractKeys.detail(id), 'taxes'] as const,
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Get contracts list with optional filters
 */
export function useContracts(query?: ContractQuery) {
  return useQuery({
    queryKey: contractKeys.list(query),
    queryFn: () => contractService.getContracts(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Get single contract by ID
 */
export function useContract(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: () => contractService.getContractById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get contract with all details (line items, addons, discounts, taxes)
 */
export function useContractWithDetails(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: () => contractService.getContractWithDetails(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get contract line items
 */
export function useContractLineItems(contractId: string) {
  return useQuery({
    queryKey: contractKeys.lineItems(contractId),
    queryFn: () => contractService.getLineItems(contractId),
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get contract addons
 */
export function useContractAddons(contractId: string) {
  return useQuery({
    queryKey: contractKeys.addons(contractId),
    queryFn: () => contractService.getAddons(contractId),
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get contract discounts
 */
export function useContractDiscounts(contractId: string) {
  return useQuery({
    queryKey: contractKeys.discounts(contractId),
    queryFn: () => contractService.getDiscounts(contractId),
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Get contract taxes
 */
export function useContractTaxes(contractId: string) {
  return useQuery({
    queryKey: contractKeys.taxes(contractId),
    queryFn: () => contractService.getTaxes(contractId),
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create new contract
 */
export function useCreateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateContract) => contractService.createContract(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract created successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to create contract')
    },
  })
}

/**
 * Update contract
 */
export function useUpdateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContract }) =>
      contractService.updateContract(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract updated successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to update contract')
    },
  })
}

/**
 * Delete contract
 */
export function useDeleteContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => contractService.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract deleted successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to delete contract')
    },
  })
}

/**
 * Activate contract
 */
export function useActivateContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => contractService.activateContract(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract activated successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to activate contract')
    },
  })
}

/**
 * Pause contract
 */
export function usePauseContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => contractService.pauseContract(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract paused successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to pause contract')
    },
  })
}

/**
 * Cancel contract
 */
export function useCancelContract() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      reason,
      cancellationType,
    }: {
      id: string
      reason?: string
      cancellationType?: 'immediate' | 'end_of_period'
    }) => contractService.cancelContract(id, reason, cancellationType),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() })
      toast.success('Contract cancelled successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to cancel contract')
    },
  })
}

// ============================================================================
// ADDON MUTATIONS
// ============================================================================

/**
 * Add addon to contract
 */
export function useAddAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      addon,
    }: {
      contractId: string
      addon: AddAddonRequest
    }) => contractService.addAddon(contractId, addon),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.addons(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.lineItems(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Addon added successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to add addon')
    },
  })
}

/**
 * Update addon
 */
export function useUpdateAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      addonId,
      updates,
    }: {
      contractId: string
      addonId: string
      updates: Partial<ContractAddon>
    }) => contractService.updateAddon(contractId, addonId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.addons(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Addon updated successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to update addon')
    },
  })
}

/**
 * Remove addon
 */
export function useRemoveAddon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      addonId,
    }: {
      contractId: string
      addonId: string
    }) => contractService.deleteAddon(contractId, addonId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.addons(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.lineItems(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Addon removed successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to remove addon')
    },
  })
}

// ============================================================================
// DISCOUNT MUTATIONS
// ============================================================================

/**
 * Add discount to contract
 */
export function useAddDiscount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      discount,
    }: {
      contractId: string
      discount: ContractDiscount
    }) => contractService.addDiscount(contractId, discount),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.discounts(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Discount added successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to add discount')
    },
  })
}

/**
 * Remove discount
 */
export function useRemoveDiscount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      discountId,
    }: {
      contractId: string
      discountId: string
    }) => contractService.deleteDiscount(contractId, discountId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.discounts(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Discount removed successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to remove discount')
    },
  })
}

// ============================================================================
// TAX MUTATIONS
// ============================================================================

/**
 * Add tax to contract
 */
export function useAddTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      tax,
    }: {
      contractId: string
      tax: ContractTax
    }) => contractService.addTax(contractId, tax),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.taxes(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Tax added successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to add tax')
    },
  })
}

/**
 * Remove tax
 */
export function useRemoveTax() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      contractId,
      taxId,
    }: {
      contractId: string
      taxId: string
    }) => contractService.deleteTax(contractId, taxId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.taxes(variables.contractId),
      })
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.contractId),
      })
      toast.success('Tax removed successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.message || 'Failed to remove tax')
    },
  })
}

// ============================================================================
// FILTER HOOK
// ============================================================================

/**
 * Hook to manage contract list filters
 */
export function useContractFilters() {
  const [filters, setFilters] = useState<ContractFilters>({
    status: 'all',
    customer_id: 'all',
    plan_id: 'all',
    billing_interval: 'all',
    page: 1,
    limit: 20,
  })

  const updateFilters = (newFilters: Partial<ContractFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  // Convert UI filters to API query
  const toAPIQuery = (uiFilters: ContractFilters): ContractQuery => {
    const query: ContractQuery = {}

    // Search
    if (uiFilters.search) {
      query.search = uiFilters.search
    }

    // Status (filter out 'all')
    if (uiFilters.status && uiFilters.status !== 'all') {
      query.status = uiFilters.status as ContractStatus
    }

    // Customer (filter out 'all')
    if (uiFilters.customer_id && uiFilters.customer_id !== 'all') {
      query.customer_id = uiFilters.customer_id
    }

    // Plan (filter out 'all')
    if (uiFilters.plan_id && uiFilters.plan_id !== 'all') {
      query.plan_id = uiFilters.plan_id
    }

    // Billing Interval (filter out 'all')
    if (uiFilters.billing_interval && uiFilters.billing_interval !== 'all') {
      query.billing_interval = uiFilters.billing_interval as BillingPeriod
    }

    // Pagination
    if (uiFilters.page) {
      query.page = uiFilters.page
    }

    if (uiFilters.limit) {
      query.limit = uiFilters.limit
    }

    // Sorting
    if (uiFilters.sortBy) {
      query.sort = uiFilters.sortBy as ContractQuery['sort']
    }

    if (uiFilters.sortOrder) {
      query.order = uiFilters.sortOrder
    }

    return query
  }

  const apiQuery = toAPIQuery(filters)

  return {
    filters,
    updateFilters,
    apiQuery,
  }
}
